/**
 * pojisteni-section-forms — the five insurance blocks of one fleet car (povinné ručení, havarijní,
 * skla, zavazadla, posádka), the body of `PojisteniModal`. Mirrors the legacy form and the API's rules:
 * an included insurance names its insurer, policy number and limit; only havarijní and skla have a
 * deductible; a block that is not included shows (and the server keeps) no details.
 * Renders `<form id={POJISTENI_FORM_ID}>`; the modal chrome supplies the submit button via the `form` attribute.
 */

import type { FormEvent } from 'react';
import { Checkbox } from '@/components/Checkbox';
import { Field, Fields } from '@/components/Field';
import { TextInput } from '@/components/TextInput';
import { formatInt } from '@/lib/format';
import { useSeededSlice, type BodyProps } from '@/modules/vozidla/vozidlo-section-forms';
import type { PojisteniBlok, PojisteniTyp } from './api';

export const POJISTENI_FORM_ID = 'pujcovna-pojisteni-form';

/** The API's fixed order; also the order of the sections. */
export const POJISTENI_TYPY: readonly PojisteniTyp[] = ['povinne_ruceni', 'havarijni', 'skla', 'zavazadla', 'posadka'];

export const POJISTENI_NAZEV: Record<PojisteniTyp, string> = {
  povinne_ruceni: 'Povinné ručení',
  havarijni: 'Havarijní pojištění',
  skla: 'Pojištění skel',
  zavazadla: 'Pojištění zavazadel',
  posadka: 'Pojištění posádky',
};

/** Only these carry a deductible, as in the legacy contract. */
export const SE_SPOLUUCASTI: readonly PojisteniTyp[] = ['havarijni', 'skla'];

const MAX_CENA = 0xffffff;
const MAX_LIMIT = 999_999_999;

/** Numbers are typed as text (digits only) and converted on save. */
export interface PojisteniBlokValue {
  v_cene: boolean;
  cena: string;
  pojistovna: string;
  cislo_smlouvy: string;
  limit_plneni: string;
  spoluucast_proc: string;
  spoluucast_kc: string;
}
export type PojisteniValue = Record<PojisteniTyp, PojisteniBlokValue>;

const num = (n: number) => (n > 0 ? String(n) : '');
const digits = (s: string) => s.replace(/\D/g, '');
const toInt = (s: string) => Number(digits(s) || '0');

const EMPTY_BLOK: PojisteniBlokValue = {
  v_cene: false,
  cena: '',
  pojistovna: '',
  cislo_smlouvy: '',
  limit_plneni: '',
  spoluucast_proc: '',
  spoluucast_kc: '',
};

export function pojisteniSeed(blocks?: readonly PojisteniBlok[] | null): PojisteniValue {
  const by = new Map((blocks ?? []).map((b) => [b.typ, b]));
  return Object.fromEntries(
    POJISTENI_TYPY.map((typ) => {
      const b = by.get(typ);
      const v: PojisteniBlokValue = b
        ? {
            v_cene: b.v_cene,
            cena: num(b.cena),
            pojistovna: b.pojistovna,
            cislo_smlouvy: b.cislo_smlouvy,
            limit_plneni: num(b.limit_plneni),
            spoluucast_proc: num(b.spoluucast_proc),
            spoluucast_kc: num(b.spoluucast_kc),
          }
        : EMPTY_BLOK;
      return [typ, v];
    }),
  ) as PojisteniValue;
}

/** All five blocks; one that is not included is sent empty (the server keeps no details for it either). */
export function pojisteniToBody(value: PojisteniValue): PojisteniBlok[] {
  return POJISTENI_TYPY.map((typ): PojisteniBlok => {
    const v = value[typ];
    if (!v.v_cene) {
      return { typ, v_cene: false, cena: 0, pojistovna: '', cislo_smlouvy: '', limit_plneni: 0, spoluucast_proc: 0, spoluucast_kc: 0 };
    }
    const deductible = SE_SPOLUUCASTI.includes(typ);
    return {
      typ,
      v_cene: true,
      cena: toInt(v.cena),
      pojistovna: v.pojistovna.trim(),
      cislo_smlouvy: v.cislo_smlouvy.trim(),
      limit_plneni: toInt(v.limit_plneni),
      spoluucast_proc: deductible ? toInt(v.spoluucast_proc) : 0,
      spoluucast_kc: deductible ? toInt(v.spoluucast_kc) : 0,
    };
  });
}

/**
 * The blocks the user actually changed against what the modal was seeded with (compared as they would be
 * sent). The save names only these, so another member's change to a block this user never touched is not
 * written back from a snapshot.
 */
export function pojisteniChanges(seed: PojisteniValue, value: PojisteniValue): PojisteniBlok[] {
  const was = new Map(pojisteniToBody(seed).map((b) => [b.typ, JSON.stringify(b)]));
  return pojisteniToBody(value).filter((b) => was.get(b.typ) !== JSON.stringify(b));
}

/** The first rule the API would refuse, in Czech, or null. The server stays the authority. */
export function pojisteniProblem(value: PojisteniValue): string | null {
  for (const typ of POJISTENI_TYPY) {
    const v = value[typ];
    if (!v.v_cene) continue;
    const name = POJISTENI_NAZEV[typ];
    if (!v.pojistovna.trim()) return `${name}: vyplňte pojišťovnu.`;
    if (!v.cislo_smlouvy.trim()) return `${name}: vyplňte číslo smlouvy.`;
    if (toInt(v.limit_plneni) < 1) return `${name}: vyplňte limit plnění.`;
    if (toInt(v.limit_plneni) > MAX_LIMIT) return `${name}: limit plnění je příliš vysoký.`;
    if (toInt(v.cena) > MAX_CENA) return `${name}: cena je příliš vysoká.`;
    if (SE_SPOLUUCASTI.includes(typ)) {
      if (toInt(v.spoluucast_proc) > 100) return `${name}: spoluúčast nemůže přesáhnout 100 %.`;
      if (toInt(v.spoluucast_kc) > MAX_CENA) return `${name}: spoluúčast je příliš vysoká.`;
    }
  }
  return null;
}

interface PojisteniFormBodyProps extends BodyProps<PojisteniValue> {
  /** Read-only members see the blocks but cannot change them. */
  disabled?: boolean;
}

export function PojisteniFormBody({ initial, busy, onSubmit, onChange, disabled = false }: PojisteniFormBodyProps) {
  const { form, set } = useSeededSlice(initial, onChange);
  // While the save is in flight the payload is already captured and the modal closes on success: an edit
  // typed now would be silently dropped, so the controls freeze until the request settles.
  const locked = disabled || busy;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (locked) return;
    onSubmit?.(form);
  };
  const patch = (typ: PojisteniTyp, p: Partial<PojisteniBlokValue>) => set(typ, { ...form[typ], ...p });
  const numeric = (typ: PojisteniTyp, key: keyof PojisteniBlokValue) => ({
    value: String(form[typ][key]),
    inputMode: 'numeric' as const,
    disabled: locked,
    onChange: (e: { currentTarget: HTMLInputElement }) => patch(typ, { [key]: digits(e.currentTarget.value) }),
  });

  return (
    <form id={POJISTENI_FORM_ID} onSubmit={submit} className="flex flex-col">
      {POJISTENI_TYPY.map((typ) => {
        const v = form[typ];
        return (
          <section key={typ} data-testid={`pojisteni-${typ}`} className="flex flex-col gap-3 border-b border-line py-4 first:pt-0 last:border-b-0">
            <Checkbox
              label={POJISTENI_NAZEV[typ]}
              checked={v.v_cene}
              disabled={locked}
              onChange={(e) => patch(typ, { v_cene: e.currentTarget.checked })}
            />
            {v.v_cene && (
              <Fields cols={2}>
                <Field label="Pojišťovna" required>
                  <TextInput
                    value={v.pojistovna}
                    maxLength={63}
                    disabled={locked}
                    onChange={(e) => patch(typ, { pojistovna: e.currentTarget.value })}
                  />
                </Field>
                <Field label="Číslo smlouvy" required>
                  <TextInput
                    value={v.cislo_smlouvy}
                    maxLength={63}
                    disabled={locked}
                    onChange={(e) => patch(typ, { cislo_smlouvy: e.currentTarget.value })}
                  />
                </Field>
                <Field label="Limit plnění" required hint={v.limit_plneni ? `${formatInt(toInt(v.limit_plneni))} Kč` : undefined}>
                  <TextInput {...numeric(typ, 'limit_plneni')} suffix="Kč" />
                </Field>
                <Field
                  label="Cena pojištění"
                  optional="volitelné"
                  hint="Jen informativně, do ceny nájmu se nepromítá."
                >
                  <TextInput {...numeric(typ, 'cena')} suffix="Kč" />
                </Field>
                {SE_SPOLUUCASTI.includes(typ) && (
                  <>
                    <Field label="Spoluúčast (%)" optional="volitelné">
                      <TextInput {...numeric(typ, 'spoluucast_proc')} suffix="%" />
                    </Field>
                    <Field label="Spoluúčast, min. (Kč)" optional="volitelné">
                      <TextInput {...numeric(typ, 'spoluucast_kc')} suffix="Kč" />
                    </Field>
                  </>
                )}
              </Fields>
            )}
          </section>
        );
      })}
    </form>
  );
}
